// @ts-nocheck
import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconChevronRight, IconCheck, IconX,
} from '@tabler/icons-react';
import { IconPenSign } from '../CustomerZoneShared';
import moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import Fetch from '@/utils/Fetch';
import JobService from '@/services/job/job-service';
import AttachmentService from '@/services/attachment-service';
import * as Enums from '@/utils/enums';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import styles from '../CustomerZoneDetail.module.css';

const SIG_MARK = (
  <svg viewBox="0 0 82 22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 14c4-9 7-9 9-2s4 8 6 1 4-9 6-2 4 8 6 2 4-9 6-2 4 8 6 2 5-8 7-3 5 6 9 3" />
  </svg>
);

type Phase = 'loading' | 'intro' | 'sign' | 'done' | 'resign';

export default function CustomerZoneSignaturePage() {
  const router = useRouter();
  const { t, c, i: id } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [notification, setNotification] = useState<null | { msg: string; danger?: boolean }>(null);
  const notifTimer = useRef<any>(null);

  const showNotification = (msg: string, danger = false) => {
    setNotification({ msg, danger });
    clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => setNotification(null), 5000);
  };

  const [phase, setPhase] = useState<Phase>('loading');
  const [signerName, setSignerName] = useState('');
  const [existingSig, setExistingSig] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [padData, setPadData] = useState<string | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(600);
  const [sigHistory, setSigHistory] = useState<any[]>([]);

  const signatureRef = useRef<any>(null);
  const padContainerRef = useRef<HTMLDivElement>(null);

  // ── Data ─────────────────────────────────────────────────────────────────────

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: jobData } = useQuery(
    ['czSignatureJob', t, c, id],
    () => JobService.getJobCustomerZone(id, c, t, tenant.API),
    { enabled: !!tenant && !!id }
  );

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  useEffect(() => {
    if (!jobData || !tenant) return;
    pageContext?.updateTenantName?.(jobData.headerTenantName);
    const name = jobData.Contact
      ? `${jobData.Contact.FirstName || ''} ${jobData.Contact.LastName || ''}`.trim()
      : jobData.CustomerName || '';
    if (name) setSignerName(name);
    loadExistingSig();
  }, [jobData]);

  const loadExistingSig = async () => {
    const { customerSignature, customerSignatureHistory } = await AttachmentService.getSignaturesCustomerZone(c, t, tenant.API, id);
    if (customerSignature) {
      setExistingSig(customerSignature);
      setSigHistory(customerSignatureHistory || [customerSignature]);
      setPhase('done');
    } else {
      setPhase('intro');
    }
  };

  // ── Canvas sizing ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if ((phase === 'sign' || phase === 'resign') && padContainerRef.current) {
      const w = padContainerRef.current.offsetWidth;
      if (w > 0) setCanvasWidth(w);
    }
  }, [phase]);

  // ── Pad ───────────────────────────────────────────────────────────────────────

  const onPadEnd = useCallback(() => {
    if (!signatureRef.current) return;
    const data = signatureRef.current.toDataURL().replace('data:image/png;base64,', '');
    setPadData(data);
  }, []);

  const clearPad = () => {
    signatureRef.current?.clear();
    setPadData(null);
  };

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!padData) return;
    setSaving(true);
    try {
      // On initial sign, clear any stale attachment; on re-sign keep the old one as history
      if (phase !== 'resign' && existingSig?.ID) {
        await AttachmentService.deleteAttachmentCustomerZone(c, t, tenant.API, existingSig.ID);
      }
      const saved = await AttachmentService.saveSignatureCustomerZone(
        c, t, tenant.API, id, Enums.Module.JobCard, padData, false, null, null
      );
      if (jobData) {
        await JobService.updateJobCustomerZone(c, t, tenant.API, { ...jobData, SignatureName: signerName }, null);
      }
      setPadData(null);
      const { customerSignature: latest, customerSignatureHistory: history } = await AttachmentService.getSignaturesCustomerZone(c, t, tenant.API, id);
      setExistingSig(latest || saved || {});
      setSigHistory(history || []);
      setPhase('done');
      showNotification('Signature captured successfully');
    } finally {
      setSaving(false);
    }
  };

  // ── Navigation ────────────────────────────────────────────────────────────────

  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';
  const enterResign = () => {
    setShowModal(false);
    setPadData(null);
    setPhase('resign');
  };

  if (!tenant || !jobData) return null;

  const jobNumber = jobData.JobCardNumber;
  const isResign = phase === 'resign';

  // ── Intro render ("who is signing?") ─────────────────────────────────────────

  const renderIntro = () => (
    <div className={styles.detail}>
      <h1 className={`${styles.dId} ${styles.signCenterTitle}`}>Signature for {jobNumber}</h1>
      <div className={styles.signCard}>
        <div className={`${styles.signGlyph} ${styles.signGlyphBlue}`}>
          <IconPenSign size={21} />
        </div>
        <span className={styles.signQ}>Who is signing this document?</span>
        <div className={styles.signField}>
          <label className={styles.signLabel} htmlFor="intro-signer">Customer *</label>
          <input
            id="intro-signer"
            className={styles.signInput}
            type="text"
            value={signerName}
            onChange={e => setSignerName(e.target.value)}
          />
        </div>
        <button
          className={`${styles.act} ${styles.actSign} ${styles.signSubmit}`}
          type="button"
          onClick={() => setPhase('sign')}
          disabled={!signerName.trim()}
        >
          <IconPenSign size={14} /><span>Sign</span>
        </button>
      </div>
    </div>
  );

  // ── Pad render ────────────────────────────────────────────────────────────────

  const renderPad = () => (
    <div className={styles.detail}>
      <div className={styles.detailHeader}>
        <h1 className={styles.dId}>{isResign ? 'Re-sign' : 'Signature'}: {jobNumber}</h1>
      </div>

      <div className={styles.signField}>
        <label className={styles.signLabel} htmlFor="pad-signer">Signed by *</label>
        <input
          id="pad-signer"
          className={styles.signInput}
          type="text"
          value={signerName}
          onChange={e => setSignerName(e.target.value)}
        />
      </div>

      <p className={styles.signAttest}>
        I, <strong>{signerName || 'the undersigned'}</strong>, confirm that the job described above has been completed to my satisfaction.
      </p>

      <div className={styles.signPadShell}>
        <div className={styles.signPadCard} ref={padContainerRef}>
          <SignatureCanvas
            ref={signatureRef}
            onEnd={onPadEnd}
            canvasProps={{ width: canvasWidth, height: 240, className: styles.signPadCanvas }}
          />
          <div className={styles.signBaseline} />
          <span className={styles.signHint}>Sign above the line</span>
        </div>
        <div className={styles.signPadActions}>
          <button className={styles.act} type="button" onClick={clearPad} disabled={!padData}>
            Clear
          </button>
          {isResign ? (
            <button className={styles.act} type="button" onClick={() => setPhase('done')}>
              Cancel
            </button>
          ) : (
            <button className={styles.act} type="button" onClick={() => setPhase('intro')}>
              Back
            </button>
          )}
          <button
            className={`${styles.act} ${styles.actSign}`}
            type="button"
            onClick={handleConfirm}
            disabled={!padData || saving}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Done render ───────────────────────────────────────────────────────────────

  const renderDone = () => {
    const records = sigHistory.length > 0
      ? sigHistory.map((sig, i) => ({
          action: i === 0 ? 'Signed' : 'Re-signed',
          date: moment(sig.CreatedDate || sig.ModifiedDate).format('D MMM YYYY, HH:mm'),
        }))
      : [{
          action: 'Signed',
          date: existingSig?.CreatedDate ? moment(existingSig.CreatedDate).format('D MMM YYYY, HH:mm') : moment().format('D MMM YYYY, HH:mm'),
        }];

    const isResigned = records.length > 1;

    return (
      <div className={styles.detail}>
        <h1 className={`${styles.dId} ${styles.signCenterTitle}`}>Signature for {jobNumber}</h1>
        <div className={styles.signCard}>
          <div className={`${styles.signGlyph} ${styles.signGlyphOk}`}>
            <IconCheck size={24} strokeWidth={2.5} />
          </div>
          <span className={styles.signSuccessT}>{isResigned ? 'Re-signed successfully' : 'Signed successfully'}</span>
          <p className={styles.signSuccessP}>Your signature has been recorded for this job.</p>
          <div className={styles.signDivider} />
          <div className={styles.signDetails}>
            <div className={styles.signDetailRow}>
              <span className={styles.sdK}>Job</span>
              <span className={styles.sdV}>{jobNumber}</span>
            </div>
          </div>
          <div className={styles.signRecord}>
            {records.map((r, i) => (
              <div key={i} className={styles.signRecordRow}>
                <span className={styles.srAction}>{r.action}</span>
                <span className={styles.srMeta}>{signerName} · {r.date}</span>
              </div>
            ))}
          </div>
          <div className={styles.sigThumb}>
            {existingSig?.Url
              ? <img src={existingSig.Url} alt="Captured signature" />
              : SIG_MARK
            }
          </div>
          <div className={styles.signField}>
            <label className={styles.signLabel}>Signed by</label>
            <input className={styles.signInput} type="text" readOnly value={signerName} />
          </div>
          <button
            className={`${styles.act} ${styles.actSign} ${styles.signSubmit}`}
            type="button"
            onClick={() => setShowModal(true)}
          >
            <IconPenSign size={14} /><span>Re-sign</span>
          </button>
        </div>
      </div>
    );
  };

  // ── Page ──────────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        className={`${styles.topAlert}${notification ? ` ${styles.topAlertShow}` : ''}${notification?.danger ? ` ${styles.topAlertDanger}` : ''}`}
        aria-hidden={!notification}
        aria-live="polite"
      >
        <span className={styles.topAlertMsg}>{notification?.msg || ''}</span>
        <button className={styles.topAlertClose} onClick={() => setNotification(null)} aria-label="Dismiss">
          <IconX size={13} />
        </button>
      </div>

      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.cardToolbar}>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/customerzone" search={{ t, c }}>Home</Link>
                <span className={styles.bcSep}><IconChevronRight /></span>
                <Link to="/customerzone" search={{ t, c, tab: 'jobs', expand: '1' }}>Jobs</Link>
                <span className={styles.bcSep}><IconChevronRight /></span>
                <a href={`/customerzone/viewjob${base}&i=${id}`}>{jobNumber}</a>
                <span className={styles.bcSep}><IconChevronRight /></span>
                <span className={styles.bcCurrent}>Signature</span>
              </nav>
            </div>

        <div className={styles.cardInner}>
          {phase === 'intro' && renderIntro()}
          {(phase === 'sign' || phase === 'resign') && renderPad()}
          {phase === 'done' && renderDone()}
        </div>
      </div>

      <div className={styles.support}>
        <span className={styles.sTitle}>Need support?</span>
        <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
      </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}

      {showModal && (
        <div
          className={styles.modalScrim}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className={styles.modal} role="dialog" aria-modal="true">
            <h3 className={styles.modalTitle}>Replace signature?</h3>
            <p className={styles.modalBody}>
              The current signature on this job will be permanently replaced. On the next step you'll confirm who is re-signing, give a reason, and add a new signature. This can't be undone.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.act} type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button className={`${styles.act} ${styles.actOutlineDanger}`} type="button" onClick={enterResign}>
                Re-sign
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
